import type { z } from 'zod'
import { formatNumber, type Locale } from './format'

const messages = {
  th: {
    required: 'กรุณากรอกข้อมูล',
    number: 'กรุณากรอกตัวเลข',
    choose: 'กรุณาเลือกจากรายการ',
    format: 'รูปแบบไม่ถูกต้อง',
    email: 'อีเมลไม่ถูกต้อง',
    minLength: (n: string) => `อย่างน้อย ${n} ตัวอักษร`,
    maxLength: (n: string) => `ไม่เกิน ${n} ตัวอักษร`,
    min: (n: string) => `ต้องไม่น้อยกว่า ${n}`,
    max: (n: string) => `ต้องไม่เกิน ${n}`,
  },
  en: {
    required: 'Required',
    number: 'Enter a number',
    choose: 'Choose an option from the list',
    format: 'Invalid format',
    email: 'Invalid email address',
    minLength: (n: string) => `At least ${n} characters`,
    maxLength: (n: string) => `At most ${n} characters`,
    min: (n: string) => `Must be at least ${n}`,
    max: (n: string) => `Must be at most ${n}`,
  },
}

const blank = (input: unknown) => input === undefined || input === null || input === ''

/**
 * Plain-language validation messages, the same in forms, imports and the API:
 * `z.config({ customError: validationMessages('th') })`. Cases not covered here
 * fall back to Zod's own locale.
 */
export function validationMessages(locale: Locale): z.core.$ZodErrorMap {
  const m = messages[locale]
  const n = (value: number | bigint) => formatNumber(Number(value), { locale })
  return (issue) => {
    switch (issue.code) {
      case 'invalid_type':
        if (blank(issue.input)) return m.required
        return issue.expected === 'number' || issue.expected === 'int' ? m.number : undefined
      case 'too_small':
        if (issue.origin === 'string') return Number(issue.minimum) <= 1 ? m.required : m.minLength(n(issue.minimum))
        if (issue.origin === 'array' || issue.origin === 'set') return Number(issue.minimum) <= 1 ? m.required : undefined
        return issue.origin === 'number' || issue.origin === 'int' ? m.min(n(issue.minimum)) : undefined
      case 'too_big':
        if (issue.origin === 'string') return m.maxLength(n(issue.maximum))
        return issue.origin === 'number' || issue.origin === 'int' ? m.max(n(issue.maximum)) : undefined
      case 'invalid_value':
        return blank(issue.input) ? m.required : m.choose
      case 'invalid_format':
        return issue.format === 'email' ? m.email : m.format
      default:
        return undefined
    }
  }
}
